import React, { useEffect, useRef, useState, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { WS_URL, resolveUrl } from '../utils/api';
import CallOverlay from '../components/CallOverlay';
import { NotificationContext, CallContext, ConfirmContext } from '../components/Layout';
import { getTheme } from '../utils/theme';
import VideoTrimmer from '../components/VideoTrimmer';
import UserProfileModal from '../components/UserProfileModal';

const theme = getTheme();

const myId   = () => parseInt(localStorage.getItem('userId'));
const myName = () => localStorage.getItem('username');

function Avatar({ user, size = 40, onClick }) {
  const url  = user?.avatar ? resolveUrl(user.avatar) : null;
  const name = user?.username || '?';
  if (url) return <img src={url} alt={name} onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, cursor: onClick ? 'pointer' : 'inherit' }} onError={e => e.target.style.display='none'} />;
  return <div onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', background: theme.colors.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: Math.max(12, size * 0.38), flexShrink: 0, cursor: onClick ? 'pointer' : 'inherit' }}>{name[0]?.toUpperCase()}</div>;
}

function TickIcon({ isRead, isDelivered, isMine }) {
  if (!isMine) return null;
  if (isRead)      return <span style={{ fontSize: 10, color: '#22c55e', marginLeft: 3 }}>✓✓</span>;
  if (isDelivered) return <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 3 }}>✓✓</span>;
  return <span style={{ fontSize: 10, color: '#64748b', marginLeft: 3 }}>✓</span>;
}

// Lightbox for full-size image view
function Lightbox({ src, onClose }) {
  return (
    <div style={S.lightboxOverlay} onClick={onClose}>
      <img src={src} alt="full" style={S.lightboxImg} onClick={e => e.stopPropagation()} />
      <button style={S.lightboxClose} onClick={onClose}>✕</button>
    </div>
  );
}

export default function Chat() {
  const { userId } = useParams();
  const navigate   = useNavigate();
  const { showNotification } = useContext(NotificationContext) || {};
  const { confirm } = useContext(ConfirmContext) || {};
  const callCtx    = useContext(CallContext) || {};
  const { rtc }    = callCtx;

  const [users, setUsers]                 = useState([]);
  const [messages, setMessages]           = useState([]);
  const [newMsg, setNewMsg]               = useState('');
  const [partnerOnline, setPartnerOnline] = useState(false);
  const [ctxMenu, setCtxMenu]             = useState(null);
  const [search, setSearch]               = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [uploading, setUploading]         = useState(false);
  const [pendingFile, setPendingFile]     = useState(null); // file waiting for trim/compress
  const [showTrimmer, setShowTrimmer]     = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [isConnected, setIsConnected]     = useState(false);
  const [lightbox, setLightbox]           = useState(null);
  const [viewProfileId, setViewProfileId] = useState(null);

  const wsRef      = useRef(null);
  const bottomRef  = useRef(null);
  const fileInputRef = useRef(null);
  const token      = localStorage.getItem('token');

  const fetchUsers = useCallback(async () => {
    try { const res = await api.get('/friends_and_conversations/'); setUsers(res.data); } catch {}
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchMessages = useCallback(async () => {
    if (!userId) return;
    try { const res = await api.get(`/messages/${userId}/`); setMessages(res.data); } catch {}
  }, [userId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // WebSocket chat connection
  useEffect(() => {
    if (!userId || !token) { if (wsRef.current) wsRef.current.close(); return; }
    let ws, timer;
    const connect = () => {
      ws = new WebSocket(`${WS_URL}/ws/chat/${userId}/?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => { setIsConnected(true); };
      ws.onmessage = async (e) => {
        const data = JSON.parse(e.data);
        if (data.type === 'message') {
          setMessages(prev => {
            if (prev.find(m => m.id === data.message_id)) return prev;
            return [...prev, { id: data.message_id, sender: data.sender_id, content: data.message, timestamp: data.timestamp, file_url: data.file_url, file_type: data.file_type, original_filename: data.original_filename, is_read: data.is_read, is_delivered: true, is_unsent: false }];
          });
          if (data.sender_id !== myId()) { ws.send(JSON.stringify({ type: 'mark_read' })); }
          else { fetchUsers(); }
        } else if (data.type === 'message_unsent') {
          setMessages(prev => prev.map(m => m.id === data.message_id ? { ...m, is_unsent: true, content: null } : m));
        } else if (data.type === 'messages_deleted') {
          const ids = new Set(data.message_ids.map(id => parseInt(id)));
          setMessages(prev => prev.filter(m => !ids.has(m.id)));
        } else if (data.type === 'conversation_deleted') {
          setMessages([]);
        } else if (data.type === 'message_read') {
          setMessages(prev => prev.map(m => m.sender === myId() ? { ...m, is_read: true } : m));
        } else if (data.type === 'typing' || data.type === 'typing_status') {
          if (data.sender_id === parseInt(userId)) setOtherUserTyping(data.is_typing);
        }
      };
      ws.onclose = () => { setIsConnected(false); wsRef.current = null; timer = setTimeout(connect, 3000); };
    };
    connect();
    return () => { clearTimeout(timer); if (ws) { ws.onclose = null; ws.close(); } };
  }, [userId, token]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (userId) {
      const u = users.find(u => u.id === parseInt(userId));
      if (u) setPartnerOnline(u.is_online);
    }
  }, [users, userId]);

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true, target_id: userId }));
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false, target_id: userId }));
        }
      }, 3000);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'message', message: newMsg.trim(), sender_username: myName() }));
    setNewMsg('');
  };

  const getLimit = (f) => {
    if (f.type.startsWith('video/')) return 5 * 1024 * 1024;
    if (f.type.startsWith('image/')) return 1 * 1024 * 1024;
    return 1 * 1024 * 1024;
  };

  const sendFile = async (file) => {
    if (!file || !userId) return;

    const limit = getLimit(file);
    if (file.size > limit) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const limitMB = limit / (1024 * 1024);
      showNotification?.(`File is too large (${sizeMB}MB). Max limit for ${file.type.startsWith('video/') ? 'video' : 'this file'} is ${limitMB}MB. Please trim or compress it.`, 6000);
      setPendingFile(file);
      setShowTrimmer(true);
      return;
    }

    // Otherwise send directly
    await doSendFile(file);
  };

  const doSendFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('receiver_id', userId);
      const res = await api.post('/upload_file/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'message', message: '', file_url: res.data.file_url, file_type: res.data.file_type, original_filename: res.data.original_filename, sender_username: myName() }));
      }
    } catch (err) {
      showNotification?.('Upload failed: ' + (err.response?.data?.error || 'Error'));
    } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleTrimmerDone = (blob, name, type) => {
    setShowTrimmer(false);
    setPendingFile(null);
    const limit = getLimit({ type });
    if (blob.size > limit) {
      showNotification?.(`Processed file still exceeds ${limit / (1024 * 1024)}MB. Please trim more.`, 6000);
      return;
    }
    doSendFile(new File([blob], name, { type }));
  };

  const unsendMessage = async (msgId) => {
    const ok = await confirm({ title: 'Unsend Message?', message: 'This will remove the message for everyone.', icon: '↩️', confirmText: 'Unsend' });
    if (!ok) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'unsend_message', message_id: msgId }));
    setCtxMenu(null);
  };

  const deleteSelected = async () => {
    if (selectedMessages.size === 0) return;
    const ok = await confirm({ title: 'Delete Messages?', message: `Are you sure you want to delete ${selectedMessages.size} messages?`, icon: '🗑️', confirmText: 'Delete' });
    if (!ok) return;
    const msgIds = Array.from(selectedMessages);
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'delete_message', message_ids: msgIds }));
    setMessages(prev => prev.filter(m => !selectedMessages.has(m.id)));
    setSelectionMode(false); setSelectedMessages(new Set());
  };

  const deleteConversation = async () => {
    const ok = await confirm({ title: 'Delete Conversation?', message: 'Are you sure you want to delete the entire conversation? This cannot be undone.', icon: '💀', confirmText: 'Delete All' });
    if (!ok) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'delete_conversation' }));
  };

  const handleMsgRightClick = (e, msg) => {
    e.preventDefault();
    const isMine = msg.sender === myId();
    const elapsed = (Date.now() - new Date(msg.timestamp).getTime()) / 1000;
    setCtxMenu({ msgId: msg.id, x: e.clientX, y: e.clientY, isMine, canUnsend: isMine && elapsed <= 15 });
  };

  const selectedUser  = users.find(u => u.id === parseInt(userId));
  const filteredUsers = users.filter(u => u.username.toLowerCase().includes(search.toLowerCase()));

  // FIX: responsive layout using CSS
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = windowWidth < 768;

  return (
    <div style={S.root} onClick={() => setCtxMenu(null)}>
      {viewProfileId && <UserProfileModal userId={viewProfileId} onClose={() => setViewProfileId(null)} />}
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}

      {/* Sidebar: full width on mobile when no chat open */}
      {(!isMobile || !userId) && (
        <div style={{ ...S.sidebar, ...(isMobile ? { width: '100%' } : {}) }}>
          <div style={S.sidebarHeader}><span style={S.sidebarTitle}>💬 Messages</span></div>
          <div style={S.searchWrap}>
            <input style={S.search} placeholder="🔍 Search..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={S.userList}>
            {filteredUsers.map(u => (
              <div key={u.id} onClick={() => navigate(`/chat/${u.id}`)}
                style={{ ...S.userItem, ...(u.id === parseInt(userId) ? S.activeUser : {}) }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar user={u} size={42} onClick={e => { e.stopPropagation(); setViewProfileId(u.id); }} />
                  <div style={{ ...S.onlineDot, background: u.is_online ? '#22c55e' : '#475569' }} />
                </div>
                <div style={S.userInfo}>
                  <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...S.userName, flex: '0 1 auto' }}>{u.username}</span>
                      {u.last_message_time && <span style={{ fontSize: 10, color: '#64748b', flexShrink: 0 }}>{new Date(u.last_message_time).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</span>}
                    </div>
                    <span style={{ fontSize: 10, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.last_message || 'No messages yet'}</span>
                  </div>
                  {u.unread_count > 0 && <span style={S.badge}>{u.unread_count > 99 ? '99+' : u.unread_count}</span>}
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && <p style={S.emptyMsg}>No conversations yet.</p>}
          </div>
        </div>
      )}

      {/* Chat area */}
      {userId ? (
        <div style={{ ...S.chatArea, ...(isMobile && !userId ? { display:'none' } : {}) }}>
          <div style={S.header}>
            <div style={S.headerLeft}>
              {isMobile && <button style={S.backBtn} onClick={() => navigate('/chat')}>←</button>}
              <Avatar user={selectedUser} size={42} onClick={() => setViewProfileId(selectedUser.id)} />
              <div>
                <div style={S.headerName}>{selectedUser?.username || 'User'}</div>
                <div style={S.headerStatus}>
                  <span style={{ ...S.dot, background: partnerOnline ? '#22c55e' : '#475569' }} />
                  {partnerOnline ? 'Online' : 'Offline'}
                </div>
              </div>
            </div>
            <div style={S.headerActions}>
              {selectionMode ? (
                <>
                  <span style={{ color:theme.colors.primaryHover, fontSize: 11, alignSelf:'center' }}>{selectedMessages.size} selected</span>
                  <button style={S.iconBtn} onClick={deleteSelected}>🗑️</button>
                  <button style={S.iconBtn} onClick={() => { setSelectionMode(false); setSelectedMessages(new Set()); }}>❌</button>
                </>
              ) : (
                <>
                  <button style={S.iconBtn} title="Voice call" onClick={() => rtc?.startCall?.('audio', parseInt(userId))}>📞</button>
                  <button style={S.iconBtn} title="Video call" onClick={() => rtc?.startCall?.('video', parseInt(userId))}>📹</button>
                  <button style={S.iconBtn} title="Select messages" onClick={() => setSelectionMode(true)}>☑️</button>
                  <button style={S.iconBtn} title="Delete conversation" onClick={deleteConversation}>🗑️</button>
                </>
              )}
            </div>
          </div>

          <div style={S.messages}>
            {messages.map(msg => {
              const isMine = msg.sender === myId();
              if (msg.is_deleted_by_sender && isMine)     return null;
              if (msg.is_deleted_by_receiver && !isMine)  return null;
              return (
                <MessageBubble key={msg.id} msg={msg} isMine={isMine}
                  onRightClick={handleMsgRightClick}
                  senderUser={!isMine ? selectedUser : null}
                  selectionMode={selectionMode}
                  isSelected={selectedMessages.has(msg.id)}
                  onToggleSelect={id => setSelectedMessages(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })}
                  onImageClick={setLightbox}
                />
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* FIX: chat-input-area class makes it sticky above keyboard */}
          {otherUserTyping && (
            <div style={{ padding:'4px 14px', fontSize: 10, color:theme.colors.primaryHover, fontStyle:'italic', background:'#16161d' }}>typing...</div>
          )}
          <form className="chat-input-area" style={S.inputRow} onSubmit={sendMessage}>
            <input ref={fileInputRef} type="file" style={{ display:'none' }}
              accept="image/*,video/*,.pdf,.doc,.docx,.txt,.zip"
              onChange={e => { if (e.target.files[0]) sendFile(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" style={{ ...S.attachBtn, opacity: uploading ? 0.6 : 1 }}
              onClick={() => fileInputRef.current?.click()}>
              {uploading ? '⏳' : '📎'}
            </button>
            <textarea style={{ ...S.msgInput, resize: 'none', height: 'auto', minHeight: '38px', maxHeight: '100px', fontFamily: 'inherit' }} rows={1} value={newMsg} 
              onChange={e => { setNewMsg(e.target.value); handleTyping(); }}
              placeholder={isConnected ? 'Type a message...' : 'Connecting...'}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); } }}
              autoFocus />
            <button type="submit" onMouseDown={e => e.preventDefault()} onTouchStart={e => e.preventDefault()} style={{ ...S.sendBtn, opacity: newMsg.trim() && isConnected ? 1 : 0.5 }}>➤</button>
          </form>
        </div>
      ) : !isMobile && (
        <div style={S.empty}>
          <div style={{ fontSize: 54 }}>💬</div>
          <h2 style={{ color:'#e2e8f0', margin:'12px 0 8px' }}>Select a conversation</h2>
          <p style={{ color:'#64748b', margin:0 }}>Or meet someone in Random Chat</p>
        </div>
      )}

      {ctxMenu && (
        <div style={{ ...S.ctxMenu, left: Math.min(ctxMenu.x, window.innerWidth - 200), top: Math.min(ctxMenu.y, window.innerHeight - 120) }} onClick={e => e.stopPropagation()}>
          {ctxMenu.canUnsend && <button style={S.ctxItem} onClick={() => unsendMessage(ctxMenu.msgId)}>↩️ Unsend</button>}
        </div>
      )}

      {/* VideoTrimmer for chat file uploads */}
      {showTrimmer && pendingFile && (
        <VideoTrimmer
          file={pendingFile}
          limit={getLimit(pendingFile)}
          onDone={handleTrimmerDone}
          onCancel={() => { setShowTrimmer(false); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
        />
      )}
    </div>
  );
}

function VideoPlayer({ src }) {
  const [err, setErr] = React.useState(false);
  if (err) return <div style={{ padding: '8px 4px', fontSize: 10, color: '#64748b' }}>⚠️ Video unavailable</div>;
  return <video src={src} controls playsInline style={S.msgVideo} onError={() => setErr(true)} />;
}

function MessageBubble({ msg, isMine, onRightClick, senderUser, selectionMode, isSelected, onToggleSelect, onImageClick }) {
  const isUnsent = msg.is_unsent;
  const isImage  = msg.file_type?.startsWith('image/');
  const isVideo  = msg.file_type?.startsWith('video/');
  const isFile   = msg.file_url && !isImage && !isVideo;
  const fileUrl  = resolveUrl(msg.file_url);

  return (
    <div style={{ ...S.msgRow, justifyContent: isMine ? 'flex-end' : 'flex-start', cursor: selectionMode ? 'pointer' : 'default', opacity: selectionMode && !isSelected ? 0.6 : 1 }}
      onClick={() => selectionMode && onToggleSelect(msg.id)}
      onContextMenu={e => { if (!isUnsent && !selectionMode) onRightClick(e, msg); }}>
      {selectionMode && (
        <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${theme.colors.primaryHover}`, background: isSelected ? theme.colors.primaryHover : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', marginRight: isMine ? 0 : 8, marginLeft: isMine ? 8 : 0, order: isMine ? 2 : -1 }}>
          {isSelected && <span style={{ color:'#fff', fontSize: 10 }}>✓</span>}
        </div>
      )}
      {!isMine && <Avatar user={senderUser} size={28} />}
      <div style={{ ...S.bubble, ...(isMine ? S.myBubble : S.theirBubble), ...(isUnsent ? S.unsentBubble : {}), maxWidth: 'min(70%, 320px)' }}>
        {isUnsent ? (
          <em style={{ opacity:0.6, fontSize: 11 }}>Message unsent</em>
        ) : (
          <>
            {isImage && (
              <img src={fileUrl} alt="img" style={S.msgImage}
                onClick={() => onImageClick?.(fileUrl)}
                onError={e => { e.target.style.display='none'; }} />
            )}
            {isVideo && <VideoPlayer src={fileUrl} />}
            {isFile && <a href={fileUrl} target="_blank" rel="noreferrer" style={S.fileLink}>📎 {msg.original_filename || 'Download file'}</a>}
            {msg.content && <span style={S.msgText}>{msg.content}</span>}
          </>
        )}
        <div style={S.msgMeta}>
          <span style={S.msgTime}>{new Date(msg.timestamp).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</span>
          <TickIcon isRead={msg.is_read} isDelivered={msg.is_delivered} isMine={isMine} />
        </div>
      </div>
    </div>
  );
}

const S = {
  root: { display:'flex', height:'100%', overflow:'hidden', fontFamily:'system-ui, sans-serif', position:'relative', background:'#0a0a0f' },
  '.app-sidebar': { width: 280, background: '#111118', borderRight: `1px solid rgba(${theme.colors.primaryRGB},0.15)`, display: 'flex', flexDirection: 'column', transition: 'left 0.3s ease', height: '100%', position: 'relative', flexShrink: 0 },
  '@media (max-width: 1023px)': {
    '.app-sidebar': { position: 'fixed', left: -280, zIndex: 1000, height: '100dvh' },
    '.app-sidebar.open': { left: 0 }
  },
  '.app-header': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#111118', borderBottom: `1px solid rgba(${theme.colors.primaryRGB},0.1)`, width: '100%', maxWidth: 1400, margin: '0 auto', boxSizing: 'border-box' },
  '@media (min-width: 1400px)': {
    '.app-header': { padding: '16px 24px', borderRadius: '0 0 16px 16px', marginTop: 10, border: `1px solid rgba(${theme.colors.primaryRGB},0.15)` }
  },
  sidebar: { width:280, minWidth:200, borderRight:`1px solid rgba(${theme.colors.primaryRGB},0.15)`, display:'flex', flexDirection:'column', background:'#111118', flexShrink:0 },
  sidebarHeader: { padding:'16px 16px 8px', borderBottom:'1px solid #2d2d3d' },
  sidebarTitle: { fontSize: 14, fontWeight:700, color:'#e2e8f0' },
  searchWrap: { padding:'10px 12px' },
  search: { width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #2d2d3d', background:'#1e1e2e', color:'#e2e8f0', fontSize: 12, outline:'none', boxSizing:'border-box' },
  userList: { flex:1, overflowY:'auto' },
  userItem: { display:'flex', alignItems:'center', gap:10, padding:'10px 12px', cursor:'pointer', borderRadius:10, margin:'2px 6px' },
  activeUser: { background:'#1e1e2e' },
  onlineDot: { width:10, height:10, borderRadius:'50%', position:'absolute', bottom:0, right:0, border:'2px solid #16161d' },
  userInfo: { flex:1, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'space-between' },
  userName: { fontSize: 12, fontWeight:500, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  badge: { background:theme.colors.primaryHover, color:'#fff', borderRadius:999, padding:'1px 7px', fontSize: 10, fontWeight:700, flexShrink:0 },
  emptyMsg: { textAlign:'center', color:'#64748b', padding:24, fontSize: 11 },
  chatArea: { flex:1, display:'flex', flexDirection:'column', background:'#0f0f13', minWidth:0 },
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 16px', borderBottom:'1px solid #2d2d3d', background:'#16161d', flexShrink:0 },
  headerLeft: { display:'flex', alignItems:'center', gap:10 },
  backBtn: { background:'none', border:'none', color:theme.colors.primaryHover, fontSize: 18, cursor:'pointer', padding:'0 4px 0 0' },
  headerName: { fontWeight:600, color:'#e2e8f0', fontSize: 13 },
  headerStatus: { fontSize: 10, color:'#64748b', display:'flex', alignItems:'center', gap:4 },
  dot: { width:8, height:8, borderRadius:'50%', display:'inline-block' },
  headerActions: { display:'flex', gap:6 },
  iconBtn: { background:'#1e1e2e', border:'1px solid #2d2d3d', borderRadius:8, padding:'7px 10px', cursor:'pointer', fontSize: 14 },
  messages: { flex:1, overflowY:'auto', padding:'12px 16px', display:'flex', flexDirection:'column', gap:4 },
  msgRow: { display:'flex', alignItems:'flex-end', gap:6 },
  bubble: { padding:'9px 13px', borderRadius:16, wordBreak:'break-word' },
  myBubble: { background:'#334155', color:'#fff', borderBottomRightRadius:4 },
  theirBubble: { background:'#1e1e2e', color:'#e2e8f0', borderBottomLeftRadius:4 },
  unsentBubble: { background:'#1e1e2e', border:'1px dashed #475569' },
  msgText: { fontSize: 12, lineHeight:1.5, display:'inline' },
  msgMeta: { display:'inline-flex', alignItems:'center', gap:3, marginLeft:8, marginTop:6, float:'right' },
  msgTime: { fontSize:10, opacity:0.65 },
  msgImage: { maxWidth:'100%', maxHeight:240, borderRadius:10, cursor:'pointer', display:'block', objectFit:'cover' },
  msgVideo: { maxWidth:'100%', maxHeight:200, borderRadius:10, display:'block' },
  fileLink: { color:theme.colors.primaryHover, textDecoration:'none', fontSize: 12, display:'flex', alignItems:'center', gap:6 },
  inputRow: { display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderTop:'1px solid #2d2d3d', background:'#16161d', flexShrink:0 },
  attachBtn: { background:'#1e1e2e', border:'1px solid #2d2d3d', borderRadius:10, padding:'9px 11px', cursor:'pointer', fontSize: 14, flexShrink:0 },
  msgInput: { flex:1, padding:'10px 14px', borderRadius:12, border:'1px solid #2d2d3d', background:'#1e1e2e', color:'#e2e8f0', fontSize: 12, outline:'none', minWidth:0 },
  sendBtn: { background:theme.colors.gradient, border:'none', borderRadius:10, padding:'9px 14px', cursor:'pointer', fontSize: 14, color:'#fff', flexShrink:0 },
  ctxMenu: { position:'fixed', background:'#1e1e2e', border:'1px solid #2d2d3d', borderRadius:10, padding:6, zIndex:1000, boxShadow:'0 8px 24px rgba(0,0,0,0.4)', minWidth:160 },
  ctxItem: { display:'block', width:'100%', padding:'8px 12px', background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', textAlign:'left', fontSize: 12, borderRadius:6 },
  empty: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8 },
  // Lightbox
  lightboxOverlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.95)', zIndex:50000, display:'flex', alignItems:'center', justifyContent:'center' },
  lightboxImg: { maxWidth:'95vw', maxHeight:'90vh', objectFit:'contain', borderRadius:8 },
  lightboxClose: { position:'absolute', top:16, right:16, background:'rgba(255,255,255,0.15)', border:'none', borderRadius:'50%', width:36, height:36, color:'#fff', fontSize: 16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
};
